import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  HiChartBar,
  HiArrowRight,
  HiArrowLeft,
  HiCheck,
  HiLightningBolt,
} from 'react-icons/hi'

/**
 * Multi-step onboarding flow with clean, centered CTA-style design.
 * Inspired by Untitled UI's simple centered approach.
 *
 * Flow: Landing → Onboarding → Sign Up (with payment) → App
 */

interface OnboardingStep {
  id: string
  badge?: string
  headline: string
  description: string
  features?: string[]
  visual?: 'welcome' | 'comparison' | 'features' | 'workflow' | 'trial'
}

const STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    badge: 'Welcome to StatScout',
    headline: 'Bet smarter with data,\nnot gut feeling',
    description: 'The transparent analytics platform for bet builders. We show you why, not just what.',
    features: ['65+ betting markets', '6 major leagues', '31 bookmakers'],
    visual: 'welcome',
  },
  {
    id: 'problem',
    badge: 'The Problem',
    headline: 'Stop guessing.\nStart analysing.',
    description: 'Most bettors rely on tips and hunches. StatScout gives you the actual data behind every market.',
    features: [
      'See per-90 stats, not guesses',
      'Model probabilities vs bookmaker odds',
      'Referee history, H2H data, form windows',
    ],
    visual: 'comparison',
  },
  {
    id: 'features',
    badge: 'What You Get',
    headline: 'Professional-grade\nanalytics tools',
    description: 'Everything you need to find value and build better bets.',
    features: [
      'Dixon-Coles ML probability model',
      'Automatic value bet detection',
      'Live tips during matches',
      '50+ factor card predictions',
      'Player & team comparisons',
      'Predicted lineups with confidence %',
    ],
    visual: 'features',
  },
  {
    id: 'workflow',
    badge: 'How It Works',
    headline: 'Four steps to\nsmarter betting',
    description: 'Pick a fixture, explore the builder, find value, place your bet.',
    features: [
      '1. Browse upcoming fixtures across 6 leagues',
      '2. Tap into 65+ markets with full stats',
      '3. Spot value with edge badges',
      '4. Build your bet with correlation warnings',
    ],
    visual: 'workflow',
  },
  {
    id: 'trial',
    badge: '7-Day Free Trial',
    headline: 'Start for free.\nCancel anytime.',
    description: 'Full access to everything. No restrictions. You\'ll only be charged £7.95/month after your trial ends.',
    features: [
      'Full access to all features',
      'All 6 leagues included',
      'Cancel anytime in settings',
      'Card required to start',
    ],
    visual: 'trial',
  },
]

export function OnboardingFlow() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [direction, setDirection] = useState(1)

  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === STEPS.length - 1
  const step = STEPS[currentStep]

  const handleNext = () => {
    if (isLastStep) {
      navigate('/sign-up')
    } else {
      setDirection(1)
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (isFirstStep) {
      navigate('/')
    } else {
      setDirection(-1)
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleSkip = () => {
    navigate('/sign-up')
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext()
      } else if (e.key === 'ArrowLeft') {
        handleBack()
      } else if (e.key === 'Escape') {
        navigate('/')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentStep])

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir < 0 ? 60 : -60, opacity: 0 }),
  }

  return (
    <div className="onboarding-simple">
      {/* Background gradient */}
      <div className="onboarding-simple-bg" />

      {/* Header */}
      <header className="onboarding-simple-header">
        <Link to="/" className="onboarding-simple-logo">
          <HiChartBar />
          <span>StatScout</span>
        </Link>
        <button className="onboarding-simple-skip" onClick={handleSkip}>
          Skip
        </button>
      </header>

      {/* Progress dots */}
      <div className="onboarding-simple-progress">
        {STEPS.map((_, index) => (
          <button
            key={index}
            className={`onboarding-simple-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            onClick={() => {
              setDirection(index > currentStep ? 1 : -1)
              setCurrentStep(index)
            }}
            aria-label={`Step ${index + 1}`}
          />
        ))}
      </div>

      {/* Main content */}
      <main className="onboarding-simple-main">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="onboarding-simple-content"
          >
            {/* Badge */}
            {step.badge && (
              <span className="onboarding-simple-badge">{step.badge}</span>
            )}

            {/* Headline */}
            <h1 className="onboarding-simple-headline">
              {step.headline.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i < step.headline.split('\n').length - 1 && <br />}
                </span>
              ))}
            </h1>

            {/* Description */}
            <p className="onboarding-simple-description">{step.description}</p>

            {/* Features list */}
            {step.features && (
              <ul className="onboarding-simple-features">
                {step.features.map((feature, i) => (
                  <li key={i}>
                    <HiCheck className="onboarding-simple-check" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Trial-specific pricing display */}
            {step.visual === 'trial' && (
              <div className="onboarding-simple-price">
                <span className="price-free">£0</span>
                <span className="price-label">for 7 days</span>
                <span className="price-then">then £7.95/month</span>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer navigation */}
      <footer className="onboarding-simple-footer">
        <button
          className="onboarding-simple-btn secondary"
          onClick={handleBack}
        >
          <HiArrowLeft />
          {isFirstStep ? 'Home' : 'Back'}
        </button>

        <span className="onboarding-simple-step-count">
          {currentStep + 1} / {STEPS.length}
        </span>

        <button
          className="onboarding-simple-btn primary"
          onClick={handleNext}
        >
          {isLastStep ? (
            <>
              Start Free Trial
              <HiLightningBolt />
            </>
          ) : (
            <>
              Continue
              <HiArrowRight />
            </>
          )}
        </button>
      </footer>

      {/* Sign in link */}
      <div className="onboarding-simple-signin">
        Already have an account? <Link to="/sign-in">Sign in</Link>
      </div>
    </div>
  )
}
